import * as Cesium from 'cesium/Cesium';
import { mapBoxAccessToken } from 'js/cesiumProviderTokens';
import Vue from 'vue';

export const cesiumStates = {};

export const getCurrentState = viewId => {
    return cesiumStates[viewId];
};

export const destroyCurrentState = viewId => {
    delete cesiumStates[viewId];
};

export const createCesiumEventHandlerRegistry = () => ({
    state: {
        mouseMoveHandler: null,
        mouseRightClickHandler: null,
        mouseLabelHandler: null
    },
    set(key, value) {
        const existing = this.state[key];
        if (
            existing &&
            typeof existing.destroy === 'function' &&
            (typeof existing.isDestroyed !== 'function' ||
                existing.isDestroyed() !== true)
        ) {
            try {
                existing.destroy();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(
                    'Failed to destroy Cesium event handler before replacement',
                    error
                );
            }
        }
        this.state[key] = value;
    },
    destroyHandler(key) {
        const handler = this.state[key];
        if (
            handler &&
            typeof handler.destroy === 'function' &&
            (typeof handler.isDestroyed !== 'function' ||
                handler.isDestroyed() !== true)
        ) {
            handler.destroy();
        }
        this.state[key] = null;
    },
    destroyAll() {
        Object.keys(this.state).forEach(key => {
            this.destroyHandler(key);
        });
    }
});

export const initCesiumState = viewId => {
    cesiumStates[viewId] = {
        viewId,
        viewerState: {
            state: {
                is3D: undefined,
                viewType: undefined,
                isMobile: undefined,
                mainViewer: undefined,
                cViewer: undefined,
                cesiumURL: undefined,
                currentTileset: undefined,
                currentNativeSplatViewer: undefined,
                pointCloudColouring: undefined,
                currentViewID: undefined,
                heightOffset: 0,
                currentMode: 'select-annotation',
                currentClassification: 'info',
                currentColor: Cesium.Color.WHITE.withAlpha(0.5),
                currentColorSolid: Cesium.Color.WHITE,
                pickedObject: undefined,
                pickedObjects: [],
                forestsmartSeed: null,
                forestsmartOverlayEnabled: true,
                forestsmartDetections: [],
                forestsmartFuelHotspots: [],
                forestsmartCoordinateContext: {},
                forestsmartSelectedDetectionId: null,
                boundingSphere: new Cesium.BoundingSphere(
                    Cesium.Cartesian3.fromDegrees(153.028683, -27.454553, 0),
                    357.8818992
                ),
                layers: undefined,
                currentMeasurementWorkflow: null,
                isNavOpen: false,
                isGalleryOpen: false,
                isAnnotationCard: false,
                isBackToSource: false,
                isRotating: false,
                listeners: [],
                datasetPackage: false
            },
            get(key) {
                return this.state[key];
            },
            set(key, value) {
                this.state[key] = value;
                for (let i = 0; i < this.state.listeners.length; i++) {
                    const listen = this.state.listeners[i];
                    if (listen.key === key) {
                        listen.listener(this.state[listen.key]);
                    }
                }
            },
            pushPickedObject(entity) {
                this.state.pickedObjects.push(entity);
                for (let i = 0; i < this.state.listeners.length; i++) {
                    const listen = this.state.listeners[i];
                    if (listen.key === 'pickedObjects') {
                        listen.listener(this.state.pickedObjects);
                    }
                }
            },
            resetPickedObjects() {
                if (this.state.pickedObjects.length > 0) {
                    this.state.pickedObjects = [];
                    for (let i = 0; i < this.state.listeners.length; i++) {
                        const listen = this.state.listeners[i];
                        if (listen.key === 'pickedObjects') {
                            listen.listener(this.state.pickedObjects);
                        }
                    }
                }
            },
            addListener(key, listener) {
                this.state.listeners.push({
                    key,
                    listener
                });
            }
        },
        settings: {
            state: {
                mapboxSatellite: mapBoxAccessToken
                    ? new Cesium.MapboxImageryProvider({
                          mapId: 'mapbox.satellite',
                          accessToken: mapBoxAccessToken
                      })
                    : null,
                enableShadows: true,
                raiseDistance: 0.1,
                isPositiveRaise: true,
                maxLabelDistance: 250,
                minLabelDistance: 0,
                maxEntityDistance: 2400,
                minEntityDistance: 0,
                annotationsOn: true,
                showMouseLabel: true,
                showInspectionCameraMarkers: false,
                contoursOn: false,
                elevationOn: false,
                listeners: [],
                minHeight: 0,
                maxHeight: 0,
                syncOn: false
            },
            get() {
                return this.state;
            },
            set(key, value) {
                this.state[key] = value;

                for (let i = 0; i < this.state.listeners.length; i++) {
                    const listen = this.state.listeners[i];
                    if (listen.key === key) {
                        listen.listener(this.state[listen.key]);
                    }
                }
            },
            addListener(key, listener) {
                this.state.listeners.push({
                    key,
                    listener
                });
            }
        },
        mouseData: {
            state: {
                mousePoint: undefined,
                mouse2DPoint: undefined,
                drawEnt: undefined,
                tempPolylineEnt: undefined,
                startMousePoint: undefined
            },
            get(key) {
                return this.state[key];
            },
            set(key, value) {
                this.state[key] = value;
            },
            reset() {
                this.state.drawEnt = null;
                this.state.tempPolylineEnt = null;
            }
        },
        entityPoints: {
            state: {
                dict: {}
            },
            get() {
                return this.state.dict;
            },
            add(id, pointIds) {
                this.state.dict[id] = pointIds;
            },
            delete(id) {
                delete this.state.dict[id];
            },
            reset() {
                this.state.dict = {};
            }
        },
        sidebarData: {
            state: {
                formConfig: {
                    resolution: false,
                    baseMethod: false
                },
                label: null,
                description: null,
                resolution: null,
                baseMethod: 'perimeter-plane',
                stockpileDisplayMode: 'surface',
                classification: 'info',
                toContinue: null,
                completionAction: null,
                canCancel: false,
                sidebarOpen: false,
                sidebarComponent: 'annotationForm',
                sidebarWidth: 300,
                annotationID: null,
                listeners: [],
                heightProfilePts: [],
                heightProfileLine: [],
                movingPtProfile: null,
                plan: null,
                spinner: false
            },
            get(key) {
                return this.state[key];
            },
            // eslint-disable-next-line sonarjs/no-identical-functions
            set(key, value) {
                this.state[key] = value;
                for (let i = 0; i < this.state.listeners.length; i++) {
                    const listen = this.state.listeners[i];
                    if (listen.key === key) {
                        listen.listener(this.state[listen.key]);
                    }
                }
            },
            setFormConfig(key, value) {
                this.state.formConfig[key] = value;

                for (let i = 0; i < this.state.listeners.length; i++) {
                    const listen = this.state.listeners[i];
                    if (listen.key === `formConfig/${key}`) {
                        listen.listener(this.state.formConfig[key]);
                    }
                }
            },
            openSidebar({
                component = 'annotationForm',
                annotationID = null,
                sidebarWidth = 300,
                heightProfilePts = [],
                heightProfileLine = [],
                movingPtProfile = null,
                plan = null,
                spinner = false,
                canCancel = false
            }) {
                // pass in the component that should display in the sidebar
                // pass in annotationID when displaying the annotation info or when editing an entity
                this.set('formGeneration', (this.get('formGeneration') || 0) + 1);
                this.set('sidebarComponent', component);
                this.set('annotationID', annotationID);
                this.set('sidebarWidth', sidebarWidth);
                this.set('sidebarOpen', true);
                this.set('toContinue', false);
                this.set('completionAction', null);
                this.set('canCancel', canCancel);
                this.set('label', '');
                this.set('description', '');
                this.set('classification', 'info');
                this.set('heightProfilePts', heightProfilePts);
                this.set('heightProfileLine', heightProfileLine);
                this.set('movingPtProfile', movingPtProfile);
                this.set('plan', plan);
                this.set('spinner', spinner);
                // this.state.plans.push(plan);
            },
            completeSidebar(action = 'save') {
                this.set('sidebarOpen', false);
                this.set('completionAction', action);
                this.set('toContinue', true);
            },
            closeSidebar() {
                this.completeSidebar('save');
            },
            // eslint-disable-next-line sonarjs/no-identical-functions
            addListener(key, listener) {
                this.state.listeners.push({
                    key,
                    listener
                });
            }
        },
        creatorDateInfo: {
            state: {
                dict: {}
            },
            get() {
                return this.state.dict;
            },
            add(id, value) {
                // id is the entity id
                // value should be an object with keys: creator, date
                this.state.dict[id] = value;
            },
            delete(id) {
                delete this.state.dict[id];
            }
        },
        flags: {
            state: {
                lookUp: false,
                lookDown: false,
                lookLeft: false,
                lookRight: false,
                moveForward: false,
                moveBackward: false,
                moveUp: false,
                moveDown: false,
                moveLeft: false,
                moveRight: false,
                control: false
            },
            get() {
                return this.state;
            },
            set(key, value) {
                this.state[key] = value;
            },
            toggle(key) {
                this.state[key] = !this.state[key];
            }
        },
        buttonConfig: {
            // THIS probably isn't needed
            state: {
                shadowsBtn: true,
                toggleRaiseBtn: true,
                toggleAnnotationsBtn: true,
                toggleMouseLabel: true,
                exportAnnotationsBtn: true,
                importAnnotationsBtn: true,
                selectAnnotationBtn: true,
                drawMarkerBtn: true,
                drawLineBtn: true,
                drawPolygonBtn: true,
                wallRectangleBtn: true,
                drawCircleBtn: true,
                colorBtns: true,
                fillStockpileBtn: true,
                cutStockpileBtn: true,
                measure3DPolylineBtn: true,
                measure2DPolygonBtn: true,
                measure3DPolygonBtn: true,
                downloadImagesBtn: true,
                downloadDatasetBtn: true,
                deletePolygonBtn: true,
                deleteAllBtn: true,
                galleryBtn: true
            },
            get(key) {
                return this.state[key];
            },
            setConfig(newConfig) {
                Object.assign(this.state, newConfig);
            },
            set(key, value) {
                this.state[key] = value;
            }
        },
        crossComponentHelpers: {
            state: {
                snack: '',
                listeners: []
            },
            getSnack() {
                return this.state.snack;
            },
            setSnack(snack) {
                this.state.snack = snack;
                for (let i = 0; i < this.state.listeners.length; i++) {
                    this.state.listeners[i].listener(
                        this.state[this.state.listeners[i].key]
                    );
                }
            },
            // eslint-disable-next-line sonarjs/no-identical-functions
            addListener(key, listener) {
                this.state.listeners.push({
                    key,
                    listener
                });
            }
        },
        report: {
            state: {
                includedAnnotations: [],
                customAnnotations: [],
                reportWalkThrough: false,
                listeners: []
            },
            getIncludedAnnotations() {
                return this.state.includedAnnotations;
            },
            getCustomAnnotations() {
                return this.state.customAnnotations;
            },
            setIncludedAnnotations(newArray) {
                this.state.includedAnnotations = newArray;
            },
            setCustomAnnotations(newArray) {
                this.state.customAnnotations = newArray;
            },
            setReportWalkThrough(bool) {
                this.state.reportWalkThrough = bool;
                for (let i = 0; i < this.state.listeners.length; i++) {
                    const listen = this.state.listeners[i];
                    if (listen.key === 'reportWalkThrough') {
                        listen.listener(this.state.reportWalkThrough);
                    }
                }
            },
            // eslint-disable-next-line sonarjs/no-identical-functions
            addListener(key, listener) {
                this.state.listeners.push({
                    key,
                    listener
                });
            }
        },
        clientEntityState: {
            state: {
                clientEntities: [],
                clientIDs: [],
                kmlTracker: {},
                OldKmlTracker: {},
                disabledKmlAnnotations: [],
                disabledStandardAnnotations: [],
                classificationById: {},
                activeClassificationFilter: 'all',
                initialFetchDone: false
            },
            get() {
                return this.state;
            },
            set(key, value) {
                this.state[key] = value;
            },
            push(key, value) {
                this.state[key].push(value);
            },
            reset(key) {
                this.state[key] = [];
            },
            setKML(value, old_kml = false) {
                // Check if new value is empty and reset all data to incoming data
                if (Object.getOwnPropertyNames(value).length == 0) {
                    if (old_kml) {
                        this.state['OldKmlTracker'] = value;
                    } else {
                        this.state['kmlTracker'] = value;
                    }
                } else {
                    if (old_kml) {
                        // Set key object and old key will be replaced with new object with same key
                        Object.assign(this.state['OldKmlTracker'], value);
                    } else {
                        // Set key object and old key will be replaced with new object with same key
                        Object.assign(this.state['kmlTracker'], value);
                    }
                }
            },
            deleteKMLEntityID(M, value) {
                // remove kmL value/clientID from Map
                var viewer = M.viewerState.get('mainViewer');

                /* The below commented section is not working for now 
                and we have opted to use browser window reload to 
                achieve an accurate result because we no longer 
                operate on real cesium entities. 
                @author: Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
                */

                // this.state.kmlTracker[value].kmlentities.map( (kmlentity, index) => {
                //     var kmlentity_entityCollection_id = kmlentity.entityCollection.id;
                //     var kmlentity_id = kmlentity.id;
                //     viewer.entities.remove(kmlentity);
                // });

                // remove value/clientID from kmlData if it exist
                delete this.state.kmlTracker[value];
            },
            disableKMLEntityID(M, value) {
                // Note - i (francis williams) disabled this feature and will work on it later
                // remove kmL value/clientID from Map without reloading page
                var viewer = M.viewerState.get('mainViewer');
                this.state.kmlTracker[value].kmlentities.map(kmlentity => {
                    viewer.entities.remove(kmlentity);
                });

                // remove value/clientID from kmlData if it exist
                delete this.state.kmlTracker[value];

                // push disabled Entity to clientEntityState disabledKmlAnnotations
                this.state.disabledKmlAnnotations.push(value);
            }
        },
        eventHandlers: createCesiumEventHandlerRegistry(),
        tempEntities: {
            state: {
                tempEntityCollection: new Cesium.CustomDataSource('temp'),
                tempPrimitiveCollection: new Cesium.PrimitiveCollection()
            },
            getCollection() {
                return this.state.tempEntityCollection;
            },
            getPrimitiveCollection() {
                return this.state.tempPrimitiveCollection;
            },
            addEntity(entity) {
                this.state.tempEntityCollection.entities.add(entity);
            },
            addPrimitive(primitive) {
                this.state.tempPrimitiveCollection.add(primitive);
            },
            clear() {
                this.state.tempEntityCollection.entities.removeAll();
                this.state.tempPrimitiveCollection.removeAll();
            }
        },
        // Persistent per-stockpile overlay visuals. Unlike tempEntities,
        // these survive selection changes; clearFor removes only the overlay
        // belonging to one stockpile entity.
        stockpileOverlays: {
            state: {
                overlayEntityCollection: new Cesium.CustomDataSource(
                    'stockpileOverlays'
                ),
                overlayPrimitiveCollection: new Cesium.PrimitiveCollection(),
                byEntity: {},
                ownerByOverlayId: {}
            },
            getCollection() {
                return this.state.overlayEntityCollection;
            },
            getPrimitiveCollection() {
                return this.state.overlayPrimitiveCollection;
            },
            getRegistry(entityID) {
                if (!this.state.byEntity[entityID]) {
                    this.state.byEntity[entityID] = {
                        entities: [],
                        primitives: []
                    };
                }
                return this.state.byEntity[entityID];
            },
            addEntity(entityID, entity) {
                const added = this.state.overlayEntityCollection.entities.add(
                    entity
                );
                this.getRegistry(entityID).entities.push(added);
                this.state.ownerByOverlayId[added.id] = entityID;
                return added;
            },
            addPrimitive(entityID, primitive) {
                this.state.overlayPrimitiveCollection.add(primitive);
                this.getRegistry(entityID).primitives.push(primitive);
                return primitive;
            },
            // Track which display mode each stockpile's overlay was last
            // rendered with so redundant rebuilds (e.g. re-selecting the
            // same stockpile) can be skipped.
            setDisplayMode(entityID, displayMode) {
                this.getRegistry(entityID).displayMode = displayMode;
            },
            getDisplayMode(entityID) {
                const registry = this.state.byEntity[entityID];
                return registry ? registry.displayMode || null : null;
            },
            // Resolve a picked id (overlay entity id, or a drape geometry
            // instance id carrying the owner id) back to the owning
            // stockpile annotation id. Returns null for non-overlay picks.
            getOwnerId(rawId) {
                if (rawId === undefined || rawId === null) {
                    return null;
                }
                if (this.state.byEntity[rawId]) {
                    return rawId;
                }
                return this.state.ownerByOverlayId[rawId] || null;
            },
            clearFor(entityID) {
                const registry = this.state.byEntity[entityID];
                if (!registry) {
                    return;
                }
                registry.entities.forEach(entity => {
                    delete this.state.ownerByOverlayId[entity.id];
                    this.state.overlayEntityCollection.entities.remove(entity);
                });
                registry.primitives.forEach(primitive => {
                    this.state.overlayPrimitiveCollection.remove(primitive);
                });
                delete this.state.byEntity[entityID];
            },
            setVisible(visible) {
                this.state.overlayEntityCollection.show = visible;
                this.state.overlayPrimitiveCollection.show = visible;
            },
            clear() {
                this.state.overlayEntityCollection.entities.removeAll();
                this.state.overlayPrimitiveCollection.removeAll();
                this.state.byEntity = {};
                this.state.ownerByOverlayId = {};
            }
        },
        other: {
            state: {
                secondaryEntityCollection: new Cesium.CustomDataSource(
                    'Second Collection'
                ),
                inspectionCameraMarkerCollection: new Cesium.CustomDataSource(
                    'Inspection Camera Markers'
                )
            },
            get(key) {
                return this.state[key];
            },
            set(key, value) {
                this.state[key] = value;
            }
        },
        eventBus: new Vue()
    };
};
